import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {
  AudioClipsBackendObject,
  AudioItemBackendObject,
  BaseItemBackendObject,
  EffectsBackend,
  FancyTextItemBackendObject,
  ImageItemBackendObject,
  ImageSlideItemBackendObject,
  ItemBackendObject,
  ItemTransitionBackendObject,
  LineItemBackendObject,
  MaskingFreehandBackend,
  MaskingItemBackend,
  MaskingItemsBackend,
  MaskingShapeBackend,
  MaskingTextBackend,
  MenuItemBackendObject,
  PageBackendObject,
  PageTransitionsBackendObject,
  PosterObjectBackend,
  QRCodeItemBackendObject,
  RectangleItemBackendObject,
  RemoveVideoBackgroundBackend,
  SlideBackendObject,
  SlideshowItemBackendObject,
  StickerItemBackendObject,
  SubtitleBackendObject,
  TableItemBackendObject,
  TabsItemBackendObject,
  TextItemBackendObject,
  TextSlideItemBackendObject,
  TextStylesBackend,
  TranscriptItemBackendObject,
  VectorItemBackendObject,
  VideoItemBackendObject,
  VideoSlideItemBackendObject,
} from '@PosterWhiteboard/poster/poster-backend.types';
import {colorToRgbaArray} from '@Utils/color.util';
import {GlowType, ShadowType, ShapeType} from '@PosterWhiteboard/poster/poster-backend.types';
import {FillTypes} from '@PosterWhiteboard/classes/fill.types';
import type {ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {RGBA} from '@Utils/color.util';
import {convertFabricColorToFillObject} from '@Utils/color.util';
import type {QRCodeItemObject} from '@PosterWhiteboard/items/qr-code-item.class';
import type {FancyTextItemObject} from '@PosterWhiteboard/items/fancy-text-item/fancy-text-item.class';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {StickerItemObject} from '@PosterWhiteboard/items/sticker-item.class';
import type {TextSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/text-slide-item.class';
import type {TableItemObject} from '@PosterWhiteboard/items/table-item/table-item.class';
import type {MenuItemObject} from '@PosterWhiteboard/items/menu-item/menu-item.class';
import type {TabsItemObject} from '@PosterWhiteboard/items/tabs-item/tabs-item.class';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {TransitionObject} from '@PosterWhiteboard/models/transition.class';
import type {ItemTransitionObject, PageTransitionsObject} from '@PosterWhiteboard/page/transitions/page-transitions.types';
import {ItemTransitionType} from '@PosterWhiteboard/page/transitions/page-transitions.types';
import {assertNever} from '@Utils/assert-never.util';
import type {ImageSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {MaskingItemObject} from '@PosterWhiteboard/classes/item-masking.class';
import {MaskingType} from '@PosterWhiteboard/classes/masking/masking.types';
import type {MaskingText} from '@PosterWhiteboard/classes/masking/masking-text.class';
import type {MaskingShape} from '@PosterWhiteboard/classes/masking/masking-shape.class';
import type {MaskingFreehand} from '@PosterWhiteboard/classes/masking/masking-freehand.class';
import type {RectangleItemObject} from '@PosterWhiteboard/items/rectangle-item/rectangle-item';
import type {PosterLoadObject, PosterPageObject} from '@PosterWhiteboard/poster/poster.types';
import type {VectorItemObject} from '@PosterWhiteboard/items/vector-item/vector-item.types';
import type {TextItemObject} from '@PosterWhiteboard/items/text-item/text-item.types';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import type {AudioClipsObject} from '@PosterWhiteboard/classes/audio-clips/audio-clips.class';
import {getScaleForShadowFromItemObject} from '@Utils/fabric-shadow.util';
import {radiansToDegrees} from '@Utils/math.util';
import type {SerializedShadowOptions} from '@postermywall/fabricjs-2';
import {ShadowOrGlowType} from '@postermywall/fabricjs-2';
import type {LineItemObject} from '@PosterWhiteboard/items/line-item/line-item';
import {getFirstGalleryTemplateCreatorIdFromPosterObject, getFirstGalleryTemplateIdFromPosterObject, getFirstTemplateIdFromPosterObject} from '@Libraries/poster-template-ids.library';

const CROP_VO_CLASS_NAME = 'postermywall.core.model.vo.CropVO';

export const getPosterBackendObjectFromPoster = (poster: PosterLoadObject): PosterObjectBackend => {
  if (!poster.creator) {
    throw new Error(`Poster with no creator can't be saved`);
  }

  const idGalleryTemplate = getFirstGalleryTemplateIdFromPosterObject(poster);
  const idTemplate = getFirstTemplateIdFromPosterObject(poster);

  return {
    height: poster.height,
    pages: getBackendPosterPagesObjectFromPoster(poster.pages),
    width: poster.width,
    createdOn: poster.createdOn,
    creator: poster.creator
      ? {
          id: poster.creator.id,
          fbId: poster.creator.fbId,
          name: poster.creator.name,
          type: poster.creator.type,
          watermark: poster.creator.watermark ?? '-1',
          premiumLevel: poster.creator.premiumLevel,
          preferredLanguage: poster.creator.preferredLanguage,
          verificationNeededStatus: poster.creator.verificationNeededStatus,
        }
      : null,
    description: poster.description,
    hashedID: poster.hashedID,
    id: poster.id === '' ? '-1' : poster.id,
    idGalleryTemplate: idGalleryTemplate ? idGalleryTemplate : null,
    idGalleryTemplateCreator: getFirstGalleryTemplateCreatorIdFromPosterObject(poster),
    idLastModifier: poster.idLastModifier,
    idTemplate: idTemplate === '' ? null : idTemplate,
    isCopyable: poster.isCopyable,
    isInternal: poster.isInternal,
    isPublic: poster.isPublic,
    isPurchaseable: poster.isPurchaseable,
    isTemplate: poster.isTemplate,
    lastModified: poster.lastModified,
    name: poster.name,
    seoName: poster.seoName,
    type: poster.type,
    units: poster.units,
    userHeight: poster.userHeight,
    userWidth: poster.userWidth,
    version: poster.version,
  };
};

export const getBackendPosterPagesObjectFromPoster = (pages: PosterPageObject): PageBackendObject[] => {
  const pagesBackend: PageBackendObject[] = [];

  for (const pageHashedID of pages.pageOrder) {
    const page = pages.pagesHashMap[pageHashedID];

    pagesBackend.push({
      appliedPalette: page.appliedPalette,
      templateData: page.templateData ?? null,
      galleryTemplateData: page.galleryTemplateData ?? null,
      isCustomDuration: page.isCustomDuration,
      hashedID: page.hashedID,
      graphicItems: getBackendItemsObjectForPage(page),
      audioClips: getBackendPosterAudioClipsFromPoster(page.audioClips),
      transitions: getBackendPosterTransitionsFromPoster(page.transitions),
      groupsHashMap: page.items.groups.groupsHashMap,
    });
  }

  return pagesBackend;
};

export const getBackendPosterTransitionsFromPoster = (transitions: PageTransitionsObject): PageTransitionsBackendObject => {
  const transitionsBackendHashMap: Record<string, ItemTransitionBackendObject> = {};

  for (const [key, transition] of Object.entries(transitions.transitionsHashMap)) {
    transitionsBackendHashMap[key] = getBackendTransitionFromItemTransitionObject(transition);
  }

  return {transitionsHashMap: transitionsBackendHashMap};
};

/** Wire shapes are per-type (no nullable leftovers) — each variant maps to its backend twin. */
const getBackendTransitionFromItemTransitionObject = (transition: ItemTransitionObject): ItemTransitionBackendObject => {
  const base = {
    uid: transition.uid,
    fromItemUid: transition.fromItemUid,
    toItemUid: transition.toItemUid,
    duration: transition.duration,
  };

  switch (transition.type) {
    case ItemTransitionType.FADE:
      return {...base, type: transition.type};
    case ItemTransitionType.SLIDE:
      return {...base, type: transition.type, direction: transition.direction};
    case ItemTransitionType.WIPE:
      return {...base, type: transition.type, direction: transition.direction};
    case ItemTransitionType.DIP_TO_COLOR:
      return {...base, type: transition.type, color: transition.color};
    default:
      return assertNever(transition);
  }
};

export const getBackendPosterAudioClipsFromPoster = (audioClips: AudioClipsObject): AudioClipsBackendObject => {
  const audioBackendItemsHashMap: Record<string, AudioItemBackendObject> = {};

  for (const [key, audioItemObject] of Object.entries(audioClips.audioItemsHashMap)) {
    audioBackendItemsHashMap[key] = {
      audioPlayer: {
        audioUrl: audioItemObject.audioPlayer.audioUrl,
        trim: audioItemObject.audioPlayer.trim,
        fade: audioItemObject.audioPlayer.fade,
        originalDuration: audioItemObject.audioPlayer.originalDuration,
        volume: audioItemObject.audioPlayer.volume,
        speed: audioItemObject.audioPlayer.speed,
      },
      timelineSubRow: audioItemObject.timelineSubRow,
      hashedFilename: audioItemObject.hashedFilename,
      name: audioItemObject.name,
      onPageStartTime: audioItemObject.onPageStartTime,
      source: audioItemObject.source,
      uid: audioItemObject.uid,
    };
  }
  return {
    audioItemsHashMap: audioBackendItemsHashMap,
  };
};

const getBackendItemsObjectForPage = (page: PageObject): ItemBackendObject[] => {
  const backendItemsObject = [];

  for (const hashedItemID of page.items.itemsOrder) {
    backendItemsObject.push(getBackendItemObjectForItem(page.items.itemsHashMap[hashedItemID]));
  }

  return backendItemsObject;
};

const getBackendItemObjectForItem = (item: ItemObject): ItemBackendObject => {
  switch (item.gitype) {
    case ITEM_TYPE.IMAGE:
      return getBackendImageItemObjectForItem(item as ImageItemObject);

    case ITEM_TYPE.IMAGESLIDE:
      return getBackendImageSlideItemObjectForItem(item as ImageSlideItemObject);

    case ITEM_TYPE.QR_CODE:
      return getBackendQRItemObjectForItem(item as QRCodeItemObject);

    case ITEM_TYPE.FANCY_TEXT:
      return getBackendFancyTextItemObjectForItem(item as FancyTextItemObject);

    case ITEM_TYPE.VECTOR:
      return getBackendVectorItemObjectForItem(item as VectorItemObject);

    case ITEM_TYPE.VIDEO:
      return getBackendVideoItemObjectForItem(item as VideoItemObject);

    case ITEM_TYPE.VIDEOSLIDE:
      return getBackendVideoSlideItemObjectForItem(item as VideoSlideItemObject);

    case ITEM_TYPE.STICKER:
      return getBackendStickerItemObjectForItem(item as StickerItemObject);

    case ITEM_TYPE.TEXT:
      return getBackendTextItemObjectForItem(item as TextItemObject);

    case ITEM_TYPE.TEXTSLIDE:
      return getBackendTextSlideItemObjectForItem(item as TextSlideItemObject);

    case ITEM_TYPE.TABLE:
      return getBackendTableItemObjectForItem(item as TableItemObject);

    case ITEM_TYPE.MENU:
      return getBackendMenuItemObjectForItem(item as MenuItemObject);

    case ITEM_TYPE.TAB:
      return getBackendTabsItemObjectForItem(item as TabsItemObject);

    case ITEM_TYPE.SLIDESHOW:
      return getBackendSlideshowItemObjectForItem(item as SlideshowItemObject);

    case ITEM_TYPE.TRANSCRIPT:
      return getBackendTranscriptItemObjectForItem(item as TranscriptItemObject);

    case ITEM_TYPE.RECTANGLE:
      return getBackendRectangleItemObjectForItem(item as RectangleItemObject);
    case ITEM_TYPE.LINE:
      return getBackendLineItemObjectForItem(item as LineItemObject);

    case (ITEM_TYPE.DRAWING as any):
      // Drawing item type exists (enum) but serialization is not implemented. Return a
      // minimal stub so the conversion doesn't crash on module load or during unexpected
      // poster loads that include drawing items.
      return {
        uid: (item as any).uid || `drawing-${Math.random()}`,
        gitype: ITEM_TYPE.DRAWING,
      } as any;

    default:
      throw new Error(`unhandled graphic item: ${item.gitype}`);
  }
};

const getBackendImageItemObjectForItem = (item: ImageItemObject | ImageSlideItemObject): ImageItemBackendObject => {
  if (!item.hashedFilename || !item.fileExtension) {
    throw new Error(
      `no hashedFilename or fileExtension found for image with uid: ${item.uid}. hashedFilename: ${item.hashedFilename}, fileExtension: ${item.fileExtension}, trace: ${new Error().stack}`
    );
  }
  return {
    ...getBaseBackendItemObjectFromItem(item),
    ...getBackendEffectObjectFromItem(item),
    gitype: ITEM_TYPE.IMAGE,
    hashedFilename: item.hashedFilename,
    imageUid: '',
    fileExtension: item.fileExtension,
    loadedImageHeight: item.loadedImageHeight,
    loadedImageWidth: item.loadedImageWidth,
    hasTransparency: item.hasTransparency === true,
    isUpscaled: item.isUpscaled,
    isRemoved: item.isRemoved,
    isPurchased: item.isPurchased,
    type: 0,
    imageSource: item.imageSource,
    modification: null,
    cropData: item.cropData.cropped
      ? {
          pmvcName: CROP_VO_CLASS_NAME,
          x: item.cropData.x,
          y: item.cropData.y,
          width: item.cropData.width,
          height: item.cropData.height,
          imageWidth: item.cropData.imageWidth,
          imageHeight: item.cropData.imageHeight,
          rotation: item.cropData.rotation,
        }
      : null,
    isBackgroundRemoved: item.removeBackground.isBackgroundRemoved,
    transparency: item.transparency,
    attributionName: item.attributionName,
    attributionURL: item.attributionURL,
    licenseName: item.licenseName,
    licenseURL: item.licenseURL,
    displayAttribution: item.displayAttribution,
    idUser: item.idUser,
  };
};

const getBackendImageSlideItemObjectForItem = (item: ImageSlideItemObject): ImageSlideItemBackendObject => {
  return {
    ...getBackendImageItemObjectForItem(item),
    gitype: ITEM_TYPE.IMAGESLIDE,
    slideDuration: item.slideDuration,
    horizontalAlign: item.mediaSlide.horizontalAlign,
    verticalAlign: item.mediaSlide.verticalAlign,
    flipX: false,
    flipY: false,
    alpha: 1,
  };
};

const getBackendQRItemObjectForItem = (item: QRCodeItemObject): QRCodeItemBackendObject => {
  return {
    ...getBaseBackendItemObjectFromItem(item),
    gitype: ITEM_TYPE.QR_CODE,
    message: item.message,
    qrType: item.qrType,
    foregroundFill: item.foregroundFill,
    backgroundFill: item.backgroundFill,
    margin: item.margin,
    backgroundCornerRadius: item.backgroundCornerRadius,
    dotStyle: item.dotStyle,
    eyeFrameStyle: item.eyeFrameStyle,
    eyeBallStyle: item.eyeBallStyle,
    logoHashedFilename: item.logoHashedFilename,
    logoFileExtension: item.logoFileExtension,
    logoSource: item.logoSource,
    logoSizeRatio: item.logoSizeRatio,
  };
};

const getBackendRectangleItemObjectForItem = (item: RectangleItemObject): RectangleItemBackendObject => {
  return {
    ...getBaseBackendItemObjectFromItem(item),
    gitype: ITEM_TYPE.RECTANGLE,
    fill: convertFabricColorToFillObject(item.fill),
    rx: item.rx,
    ry: item.ry,
  };
};

const getBackendLineItemObjectForItem = (item: LineItemObject): LineItemBackendObject => {
  return {
    ...getBaseBackendItemObjectFromItem(item),
    gitype: ITEM_TYPE.LINE,
    fill: convertFabricColorToFillObject(item.fill),
    lineDashType: item.lineDashType,
    lineCapStart: item.lineCapStart,
    lineCapEnd: item.lineCapEnd,
    isRounded: item.isRounded,
    customDashSize: item.customDashSize,
    customGapSize: item.customGapSize,
  };
};

const getBackendVectorItemObjectForItem = (item: VectorItemObject): VectorItemBackendObject => {
  return {
    ...getBaseBackendItemObjectFromItem(item),
    gitype: ITEM_TYPE.VECTOR,
    fillColor: convertFabricColorToFillObject(item.fill).fillColor[0],
    fileName: item.fileName,
    isComplexSVG: !item.isCustomisable,
    forceUniformFill: item.forceUniformFill,
    source: item.source,
    strokeColor: item.border.solidBorderColor,
    strokeWeight: item.border.solidBorderThickness,
  };
};

const getBackendVideoItemObjectForItem = (item: VideoItemObject): VideoItemBackendObject => {
  return {
    ...getBaseBackendItemObjectFromItem(item),
    ...getBackendEffectObjectFromItem(item),
    removeBackground: getBackendRemovedVideoBackgroundObjectFromItem(item),
    gitype: ITEM_TYPE.VIDEO,
    duration: item.duration,
    endTime: item.endTime,
    fileExtension: item.fileExtension,
    frameRate: item.frameRate,
    hasTransparency: item.hasTransparency,
    hashedFilename: item.hashedFilename,
    // isMuted: item.isMuted,
    startTime: item.startTime,
    videoSource: item.videoSource,
    volume: item.volume,
    speed: item.speed,
    loopStartOffset: item.loopStartOffset,
    cropData: item.cropData.cropped
      ? {
          pmvcName: CROP_VO_CLASS_NAME,
          x: item.cropData.x,
          y: item.cropData.y,
          width: item.cropData.width,
          height: item.cropData.height,
          imageWidth: item.cropData.imageWidth,
          imageHeight: item.cropData.imageHeight,
          rotation: item.cropData.rotation,
        }
      : null,
    timelineSpriteHashedFilename: item.timelineSpriteHashedFilename ?? null,
    timelineSpriteFrameWidth: item.timelineSpriteFrameWidth ?? null,
    timelineSpriteFrameHeight: item.timelineSpriteFrameHeight ?? null,
    timelineSpriteNumSprites: item.timelineSpriteNumSprites ?? null,
    timelineSpriteFps: item.timelineSpriteFps ?? null,
  };
};

const getBackendVideoSlideItemObjectForItem = (item: VideoSlideItemObject): VideoSlideItemBackendObject => {
  return {
    ...getBackendVideoItemObjectForItem(item),
    gitype: ITEM_TYPE.VIDEOSLIDE,
    slideDuration: item.slideDuration,
    horizontalAlign: item.mediaSlide.horizontalAlign,
    verticalAlign: item.mediaSlide.verticalAlign,
    flipX: false,
    flipY: false,
    alpha: 1,
    volume: item.volume,
    speed: item.speed,
  };
};

const getBackendStickerItemObjectForItem = (item: StickerItemObject): StickerItemBackendObject => {
  return {
    ...getBaseBackendItemObjectFromItem(item),
    ...getBackendEffectObjectFromItem(item),
    gitype: ITEM_TYPE.STICKER,
    duration: item.duration,
    endTime: item.duration,
    frameRate: item.frameRate,
    hasTransparency: true,
    hashedFilename: item.hashedFilename,
    isMuted: true,
    startTime: 0,
    highResAnimatedSprite: item.highResAnimatedSprite,
    screenAnimatedSprite: item.screenAnimatedSprite,
    stickerSource: item.stickerSource,
  };
};

const getBackendFancyTextItemObjectForItem = (item: FancyTextItemObject): FancyTextItemBackendObject => {
  return {
    ...getBaseBackendItemObjectFromItem(item),
    gitype: ITEM_TYPE.FANCY_TEXT,
    color1: item.colors[0] ?? -1,
    color2: item.colors[1] ?? -1,
    color3: item.colors[2] ?? -1,
    fontFamily: item.fontFamily,
    idFancyText: item.idFancyText,
    morphType: item.morphType,
    morphAmount: item.morphAmount,
    text: item.text,
  };
};

const getBackendTranscriptItemObjectForItem = (item: TranscriptItemObject): TranscriptItemBackendObject => {
  return {
    ...getBaseBackendItemObjectFromItem(item),
    gitype: ITEM_TYPE.TRANSCRIPT,
    subtitlesHashmap: getBackendSubtitleObjectHashmapFromTranscriptItemObject(item),
    verticalAlign: item.verticalAlign,
    generatedFrom: item.generatedFrom,
  };
};

const getBackendSubtitleObjectHashmapFromTranscriptItemObject = (item: TranscriptItemObject): Record<string, SubtitleBackendObject> => {
  const backendSubtitleObjectHashmap: Record<string, SubtitleBackendObject> = {};

  Object.entries(item.subtitlesHashmap).forEach(([key, subtitleObject]) => {
    backendSubtitleObjectHashmap[key] = {
      subtitleUID: subtitleObject.subtitleUID,
      text: subtitleObject.text,
      startTime: subtitleObject.startTime,
      endTime: subtitleObject.endTime,
      hasUserEdited: subtitleObject.hasUserEdited,
      sentenceTextStyles: subtitleObject.sentenceTextStyles,
      activeWordTextStyles: subtitleObject.activeWordTextStyles,
      backgroundFill: subtitleObject.backgroundFill,
      backgroundBorderRadius: subtitleObject.backgroundBorderRadius,
      selectedTemplateId: subtitleObject.selectedTemplateId,
      animationStyle: subtitleObject.animationStyle,
      words: subtitleObject.words,
      aura: {
        type: getShadowTypeFromItemFabricShadow(subtitleObject.shadow),
        dropShadowColor: getDropShadowColorFromItemFabricShadow(subtitleObject.shadow),
        dropShadowAngle: getShadowAngleFromItemFabricShadow(subtitleObject.shadow),
        dropShadowBlur: getShadowBlurFromItemFabricShadow(subtitleObject.shadow, getScaleForShadowFromItemObject(item)),
        dropShadowDistance: getShadowDistanceFromItemFabricShadow(subtitleObject.shadow, getScaleForShadowFromItemObject(item)),
      },
    };
  });

  return backendSubtitleObjectHashmap;
};

const getBackendTextItemObjectForItem = (item: TextItemObject): TextItemBackendObject => {
  return {
    ...getBaseBackendItemObjectFromItem(item),
    ...getBackendTextStylesObjectFromItem(item),
    gitype: ITEM_TYPE.TEXT,
    background: item.background,
    baseWidth: item.baseWidth,
    verticalAlign: item.verticalAlign,
    verticalPadding: item.verticalPadding,
    padding: item.padding,
    text: item.text,
    styleRuns: item.styleRuns,
    lineStyles: item.lineStyles,
  };
};

const getBackendTextSlideItemObjectForItem = (item: TextSlideItemObject): TextSlideItemBackendObject => {
  return {
    ...getBackendTextItemObjectForItem(item),
    gitype: ITEM_TYPE.TEXTSLIDE,
    slideDuration: item.slideDuration,
    flipX: false,
    flipY: false,
    alpha: 1,
    scaleX: 1,
    scaleY: 1,
  };
};

const getBackendTableItemObjectForItem = (item: TableItemObject): TableItemBackendObject => {
  return {
    ...getBaseBackendItemObjectFromItem(item),
    ...getBackendTextStylesObjectFromItem(item),
    gitype: ITEM_TYPE.TABLE,
    rows: item.rows,
    columns: item.columns,
    layoutDataMap: item.layoutDataMap,
    unusedData: item.unusedData,
    layoutStyle: item.layoutStyle,
    alternateBackgroundColor1: item.alternateBackgroundColor1,
    alternateBackgroundColor2: item.alternateBackgroundColor2,
    highlightedBackgroundColor: item.highlightedBackgroundColor,
    highlightedTextColor: item.highlightedTextColor,
    xSpacing: item.xSpacing,
    ySpacing: item.ySpacing,
    fontFamily2: item.fontFamily2,
    isBold2: item.isBold2,
    isItalic2: item.isItalic2,
    underLine2: item.underLine2,
    lineThrough2: item.lineThrough2,
    backgroundType: item.backgroundType,
    itemBackgroundColor: item.itemBackgroundColor,
  };
};

const getBackendMenuItemObjectForItem = (item: MenuItemObject): MenuItemBackendObject => {
  return {
    ...getBackendTableItemObjectForItem(item),
    gitype: ITEM_TYPE.MENU,
    itemIds: item.itemIds,
    copiedItemIds: item.copiedItemIds,
    iconsColor: item.iconsColor,
    iconsSize: item.iconsSize,
    wrappingInfo: item.wrappingInfo,
  };
};

const getBackendTabsItemObjectForItem = (item: TabsItemObject): TabsItemBackendObject => {
  return {
    ...getBaseBackendItemObjectFromItem(item),
    ...getBackendTextStylesObjectFromItem(item),
    gitype: ITEM_TYPE.TAB,
    text: item.text,
    numTabs: item.numTabs,
    separatorColor: item.separatorColor,
    separatorType: item.separatorType,
    backgroundType: item.backgroundType,
    itemBackgroundColor: item.itemBackgroundColor,
  };
};

const getBackendSlideshowItemObjectForItem = (item: SlideshowItemObject): SlideshowItemBackendObject => {
  return {
    ...getBaseBackendItemObjectFromItem(item),
    gitype: ITEM_TYPE.SLIDESHOW,
    transition: getBackendTransitionObjectFromItem(item),
    slideshowItems: getBackendSlideItemsObjectForSlideshow(item),
  };
};

const getBackendSlideItemsObjectForSlideshow = (slideshowItem: SlideshowItemObject): SlideBackendObject[] => {
  const backendSlidesArray = [];

  for (const hashedItemID of slideshowItem.slides.slidesOrder) {
    backendSlidesArray.push(getBackendItemObjectForItem(slideshowItem.slides.slidesHashMap[hashedItemID]) as SlideBackendObject);
  }

  return backendSlidesArray;
};

const getBackendEffectObjectFromItem = (item: ItemObject): EffectsBackend => {
  if (!('effects' in item)) {
    throw new Error(`Item doesnt have effect`);
  }

  return {
    brightness: item.effects.brightness,
    blackAndWhite: item.effects.blackAndWhite,
    blur: item.effects.blur,
    contrast: item.effects.contrast,
    edgeType: item.effects.edgeType,
    edgeThickness: item.effects.edgeThickness,
    gamma: {
      isEnabled: item.effects.gamma.isEnabled,
      red: item.effects.gamma.red,
      green: item.effects.gamma.green,
      blue: item.effects.gamma.blue,
    },
    invert: item.effects.invert,
    multiply: item.effects.multiply,
    multiplyColor: item.effects.multiplyColor,
    removeColor: item.effects.removeColor,
    removeColorValue: item.effects.removeColorValue,
    removeColorThickness: item.effects.removeColorThickness,
    pixelate: item.effects.pixelate,
    saturation: item.effects.saturation,
    sepia: item.effects.sepia,
    tint: item.effects.tint,
    tintColor: item.effects.tintColor,
    vibrance: item.effects.vibrance,
    maskingItems: getBackendMaskingObjectFromItem(item),
  };
};

const getGlowTypeFromItemFabricShadow = (itemFabricShadow: SerializedShadowOptions | null): GlowType => {
  if (!itemFabricShadow) {
    return GlowType.NONE;
  }

  switch (itemFabricShadow?.shadowOrGlowType) {
    case ShadowOrGlowType.STRONG_GLOW:
      return GlowType.STRONG;
    case ShadowOrGlowType.LIGHT_GLOW:
      return GlowType.LIGHT;
    default:
      return GlowType.NONE;
  }
};

const getShadowBlurFromItemFabricShadow = (itemFabricShadow: SerializedShadowOptions | null, scale: number): number => {
  if (itemFabricShadow?.blur !== undefined) {
    return Math.round(itemFabricShadow.blur / scale);
  }

  return 5;
};

const getShadowDistanceFromItemFabricShadow = (itemFabricShadow: SerializedShadowOptions | null, scale: number): number => {
  if (itemFabricShadow) {
    if (
      itemFabricShadow.shadowOrGlowType === ShadowOrGlowType.STRONG_SHADOW ||
      itemFabricShadow.shadowOrGlowType === ShadowOrGlowType.LIGHT_SHADOW ||
      itemFabricShadow.shadowOrGlowType === ShadowOrGlowType.CUSTOM_SHADOW
    ) {
      const offsetX = itemFabricShadow.offsetX / scale;
      const offsetY = itemFabricShadow.offsetY / scale;
      const distance = Math.sqrt(offsetX ** 2 + offsetY ** 2);

      return Math.round(distance);
    }
  }

  return 15;
};

const getShadowAngleFromItemFabricShadow = (itemFabricShadow: SerializedShadowOptions | null): number => {
  if (itemFabricShadow) {
    if (
      itemFabricShadow.shadowOrGlowType === ShadowOrGlowType.STRONG_SHADOW ||
      itemFabricShadow.shadowOrGlowType === ShadowOrGlowType.LIGHT_SHADOW ||
      itemFabricShadow.shadowOrGlowType === ShadowOrGlowType.CUSTOM_SHADOW
    ) {
      const angleRadians = Math.atan2(itemFabricShadow.offsetY, itemFabricShadow.offsetX);
      const angleDegrees = (radiansToDegrees(angleRadians) + 360) % 360;

      return angleDegrees;
    }
  }

  return 45;
};

const getDropShadowColorFromItemFabricShadow = (itemFabricShadow: SerializedShadowOptions | null): RGBA => {
  if (itemFabricShadow) {
    if (
      itemFabricShadow?.shadowOrGlowType === ShadowOrGlowType.LIGHT_SHADOW ||
      itemFabricShadow?.shadowOrGlowType === ShadowOrGlowType.STRONG_SHADOW ||
      itemFabricShadow?.shadowOrGlowType === ShadowOrGlowType.CUSTOM_SHADOW
    ) {
      return colorToRgbaArray(itemFabricShadow.color);
    }
  }

  return [0, 0, 0, 0.25];
};

const getGlowColorFromItemFabricShadow = (itemFabricShadow: SerializedShadowOptions | null): RGBA => {
  if (itemFabricShadow && (itemFabricShadow?.shadowOrGlowType === ShadowOrGlowType.LIGHT_GLOW || itemFabricShadow.shadowOrGlowType === ShadowOrGlowType.STRONG_GLOW)) {
    const color = colorToRgbaArray(itemFabricShadow.color);
    return [color[0], color[1], color[2], 1];
  }

  return [255, 255, 255, 1];
};

const getShadowTypeFromItemFabricShadow = (itemFabricShadow: SerializedShadowOptions | null): ShadowType => {
  if (!itemFabricShadow) {
    return ShadowType.NONE;
  }

  switch (itemFabricShadow.shadowOrGlowType) {
    case ShadowOrGlowType.LIGHT_SHADOW:
      return ShadowType.LIGHT;
    case ShadowOrGlowType.STRONG_SHADOW:
      return ShadowType.STRONG;
    case ShadowOrGlowType.CUSTOM_SHADOW:
      return ShadowType.CUSTOM;
    default:
      return ShadowType.NONE;
  }
};

const getBackendMaskingObjectFromItem = (item: ItemObject): MaskingItemsBackend => {
  if (!('masking' in item)) {
    return [];
  }

  return item.masking.maskingItem ? [getBackendMaskingItemFromMaskingItem(item.masking.maskingItem)] : [];
};

const getBackendMaskingItemFromMaskingItem = (maskingItem: MaskingItemObject): MaskingItemBackend => {
  if (maskingItem.type === MaskingType.SHAPE) {
    return getBackendShapeMaskingObjectFromMaskingItem(maskingItem as MaskingShape);
  }

  if (maskingItem.type === MaskingType.TEXT) {
    return getBackendTextMaskingObjectItemFromMaskingItem(maskingItem as MaskingText);
  }

  if (maskingItem.type === MaskingType.FREEHAND) {
    return getBackendFreehandMaskingObjectFromMaskingItem(maskingItem as MaskingFreehand);
  }

  throw new Error(`Unknown masking type`);
};

const getBackendTextMaskingObjectItemFromMaskingItem = (maskingItem: MaskingText): MaskingTextBackend => {
  return {
    type: MaskingType.TEXT,
    pmvcName: 'postermywall.core.model.vo.MaskingTextVO',
    maskEffect: maskingItem.maskEffect,
    imageWidth: maskingItem.imageWidth,
    imageHeight: maskingItem.imageHeight,
    angle: maskingItem.angle,
    height: maskingItem.height,
    width: maskingItem.width,
    left: maskingItem.left,
    top: maskingItem.top,
    scaleX: maskingItem.scaleX,
    scaleY: maskingItem.scaleY,
    text: maskingItem.text,
    fontSize: maskingItem.textStyles.fontSize,
    fontFamily: maskingItem.textStyles.fontFamily,
    charSpacing: maskingItem.textStyles.letterSpacing,
    lineHeight: maskingItem.textStyles.leading,
    textAlign: maskingItem.textStyles.textAlign,
    isBold: maskingItem.textStyles.isBold,
    isItalic: maskingItem.textStyles.isItalic,
    underLine: maskingItem.textStyles.underLine,
    lineThrough: maskingItem.textStyles.lineThrough,
  };
};

const getBackendShapeMaskingObjectFromMaskingItem = (maskingItem: MaskingShape): MaskingShapeBackend => {
  return {
    type: MaskingType.SHAPE,
    shapeName: maskingItem.shapeName,
    shapeType: ShapeType.VECTOR,
    insideMasking: maskingItem.insideMasking,
    angle: maskingItem.angle,
    height: maskingItem.height,
    width: maskingItem.width,
    left: maskingItem.left,
    top: maskingItem.top,
    scaleX: maskingItem.scaleX,
    scaleY: maskingItem.scaleY,
    pmvcName: 'postermywall.core.model.vo.MaskingShapeVO',
    maskEffect: maskingItem.maskEffect,
    imageWidth: maskingItem.imageWidth,
    imageHeight: maskingItem.imageHeight,
  };
};

const getBackendFreehandMaskingObjectFromMaskingItem = (maskingItem: MaskingFreehand): MaskingFreehandBackend => {
  return {
    pmvcName: 'postermywall.core.model.vo.MaskingFreeHandVO',
    type: MaskingType.FREEHAND,
    maskPoints: maskingItem.maskPoints,
    isMaskCurved: maskingItem.isMaskCurved,
    maskEffect: maskingItem.maskEffect,
    imageWidth: maskingItem.imageWidth,
    imageHeight: maskingItem.imageHeight,
  };
};

const getBackendTextStylesObjectFromItem = (item: ItemObject): TextStylesBackend => {
  if (!('textStyles' in item)) {
    throw new Error(`Item doesn't have textStyles`);
  }

  let gradientFillColor1: RGBA = [255, 255, 255, 1];
  let gradientFillColor2: RGBA = [184, 184, 184, 1];

  if ('fill' in item.textStyles && (item.textStyles.fill.fillType === FillTypes.LINEAR_GRADIENT || item.textStyles.fill.fillType === FillTypes.RADIAL_GRADIENT)) {
    gradientFillColor1 = item.textStyles.fill.fillColor[0];
    gradientFillColor2 = item.textStyles.fill.fillColor[1];
  }

  return {
    fontSize: item.textStyles.fontSize,
    fontFamily: item.textStyles.fontFamily,
    fontStyle: item.textStyles.fontStyle,
    fontWeight: item.textStyles.fontWeight,
    letterSpacing: item.textStyles.letterSpacing,
    fontLicense: item.textStyles.fontLicense,
    leading: item.textStyles.leading,
    textAlign: item.textStyles.textAlign,
    script: item.textStyles.script,
    isBold: item.textStyles.isBold,
    isItalic: item.textStyles.isItalic,
    underLine: item.textStyles.underLine,
    lineThrough: item.textStyles.lineThrough,
    stroke: item.textStyles.stroke,
    strokeColor: item.textStyles.strokeColor,
    strokeWidth: item.textStyles.strokeWidth,
    color: item.textStyles.fill.fillColor[0],
    fillType: item.textStyles.fill.fillType,
    gradientFillColor1,
    gradientFillColor2,
  };
};

const getBackendTransitionObjectFromItem = (item: ItemObject): TransitionObject => {
  if (!('transition' in item)) {
    throw new Error(`Item doesn't have transition`);
  }

  return {
    type: item.transition.type,
    speed: item.transition.speed,
    color: item.transition.color,
  };
};

const getBaseBackendItemObjectFromItem = (item: ItemObject): BaseItemBackendObject => {
  let gradientFillColor1: RGBA = [255, 255, 255, 1];
  let gradientFillColor2: RGBA = [255, 255, 255, 1];
  const fillObject = convertFabricColorToFillObject(item.fill);

  if (fillObject.fillType === FillTypes.LINEAR_GRADIENT || fillObject.fillType === FillTypes.RADIAL_GRADIENT) {
    gradientFillColor1 = fillObject.fillColor[0];
    gradientFillColor2 = fillObject.fillColor[1];
  }

  return {
    uid: item.uid,
    idOriginalOwner: item.idOriginalOwner ?? null,
    sourcePosterHashId: item.sourcePosterHashId,
    x: item.left,
    y: item.top,
    fill: convertFabricColorToFillObject(item.fill),
    alpha: item.alpha,
    width: item.width,
    height: item.height,
    rotation: item.angle,
    clickableLink: item.clickableLink,
    scaleX: item.scaleX,
    scaleY: item.scaleY,
    flipX: item.flipX,
    flipY: item.flipY,
    lockMovement: item.lockMovementX,
    dropShadow: getShadowTypeFromItemFabricShadow(item.shadow),
    glowType: getGlowTypeFromItemFabricShadow(item.shadow),
    glowColor: getGlowColorFromItemFabricShadow(item.shadow),
    dropShadowColor: getDropShadowColorFromItemFabricShadow(item.shadow),
    dropShadowAngle: getShadowAngleFromItemFabricShadow(item.shadow),
    dropShadowDistance: getShadowDistanceFromItemFabricShadow(item.shadow, getScaleForShadowFromItemObject(item)),
    dropShadowBlur: getShadowBlurFromItemFabricShadow(item.shadow, getScaleForShadowFromItemObject(item)),
    solidBorderType: item.border.solidBorderType,
    solidBorderColor: item.border.solidBorderColor,
    solidBorderThickness: item.border.solidBorderThickness,
    glow: 0,
    fillType: fillObject.fillType,
    gradientFillColor1,
    gradientFillColor2,
    patternFill: 0,
    onPageStartTime: item.onPageStartTime,
    onPageEndTime: item.onPageEndTime,
    timelineSubRow: item.timelineSubRow,
    introAnimation: item.introAnimation,
    outroAnimation: item.outroAnimation,
    isBackground: item.isBackground,
    groupId: item.groupId,
  };
};

const getBackendRemovedVideoBackgroundObjectFromItem = (item: VideoItemObject | VideoSlideItemObject): RemoveVideoBackgroundBackend => {
  return {
    isBackgroundRemoved: item.removeBackground.isBackgroundRemoved,
    startTime: item.removeBackground.trimData.startTime ?? 0,
    endTime: item.removeBackground.trimData.endTime ?? 0,
  };
};
